import { useEffect, useRef, useState } from "react"
import { Popover } from "@headlessui/react"
import cc from "classcat"

export function NavBar({ sections, theme = "yellow" }) {
  const navBarRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(-1)

  useEffect(() => {
    function updateActiveIndex() {
      let newActiveIndex = -1;
      if (!sections || sections.length === 0) {
        setActiveIndex(newActiveIndex);
        return;
      }

      const elements = sections.map(({ id }) => document.getElementById(id));

      if (!navBarRef.current) {
        return;
      }

      const bodyRect = document.body.getBoundingClientRect();
      const offset = bodyRect.top + navBarRef.current.offsetHeight + 1;

      for (let index = 0; index < elements.length; index += 1) {
        const element = elements[index];
        if (!element) {
          continue;
        }
        if (window.scrollY >= element.getBoundingClientRect().top - offset) {
          newActiveIndex = index;
        } else {
          break;
        }
      }

      setActiveIndex(newActiveIndex);
    }

    updateActiveIndex();

    window.addEventListener("resize", updateActiveIndex);
    window.addEventListener("scroll", updateActiveIndex, { passive: true });

    return () => {
      window.removeEventListener("resize", updateActiveIndex);
      window.removeEventListener("scroll", updateActiveIndex);
    };
  }, [sections]);

  return (
    <div ref={navBarRef} className="sticky top-0 z-10">
      <Popover className="sm:hidden">
        {({ open }) => (
          <>
            <div
              className={cc([
                "relative flex items-center px-4 py-3",
                {
                  "shadow-sm [@supports(backdrop-filter:blur(0))]:backdrop-blur":
                    !open,
                  "bg-kkm-yellow/50": theme === "yellow",
                  "bg-kkm-red/80": theme === "red",
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-yellow/50":
                    theme === "yellow" && !open,
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-red/80":
                    theme === "red" && !open,
                  "bg-kkm-green/50": theme === "green",
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-green/50":
                    theme === "green" && !open,
                },
              ])}
            >
              {!open && (
                <span
                  className={cc([
                    "text-base",
                    {
                      "font-medium text-zinc-900": ["yellow", "light"].includes(
                        theme,
                      ),
                      "font-bold text-zinc-100": ["green", "red"].includes(
                        theme,
                      ),
                    },
                  ])}
                >
                  {sections[Math.max(0, activeIndex)].title}
                </span>
              )}
              <Popover.Button
                className={cc([
                  "-mr-1 ml-auto flex h-8 w-8 items-center justify-center",
                  { "relative z-10": open },
                ])}
              >
                {!open && <span className="absolute inset-0" />}
                <span className="sr-only">Toggle navigation menu</span>
                <svg
                  aria-hidden="true"
                  className="h-6 w-6 stroke-slate-700"
                  fill="none"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path
                    d={
                      open
                        ? "M17 7 7 17M7 7l10 10"
                        : "m15 16-3 3-3-3M15 8l-3-3-3 3"
                    }
                  />
                </svg>
              </Popover.Button>
            </div>
            <Popover.Panel
              className={cc([
                "absolute inset-x-0 top-0 bg-white/95 py-2 shadow-sm [@supports(backdrop-filter:blur(0))]:backdrop-blur",
                {
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-green/50":
                    theme === "green",
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-yellow/50":
                    theme === "yellow",
                  "[@supports(backdrop-filter:blur(0))]:bg-kkm-red/80":
                    theme === "red",
                },
              ])}
            >
              {({
                close,
              }) =>
                sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex items-center px-4 py-2"
                    onClick={() => close()}
                  >
                    <span
                      className={cc([
                        "text-base",
                        {
                          "font-medium text-zinc-900": [
                            "yellow",
                            "light",
                          ].includes(theme),
                          "font-bold text-zinc-100": ["green", "red"].includes(
                            theme,
                          ),
                        },
                      ])}
                    >
                      {section.title}
                    </span>
                  </a>
                ))
              }
            </Popover.Panel>
            <div className="absolute inset-x-0 bottom-full z-10 bg-white" />
          </>
        )}
      </Popover>
      <div
        className={cc([
          "hidden sm:flex sm:h-24 sm:justify-center  sm:[@supports(backdrop-filter:blur(0))]:backdrop-blur",
          {
            "sm:bg-kkm-yellow/60 sm:[@supports(backdrop-filter:blur(0))]:bg-kkm-yellow/60":
              theme === "yellow",
            "sm:bg-kkm-red/60 sm:[@supports(backdrop-filter:blur(0))]:bg-kkm-red/60":
              theme === "red",
            "sm:bg-kkm-green/60 sm:[@supports(backdrop-filter:blur(0))]:bg-kkm-green/60":
              theme === "green",
          },
        ])}
      >
        <ol className="-mb-[2px] ml-0 grid auto-cols-[minmax(0,19rem)] grid-flow-col text-sm font-medium text-slate-900 [counter-reset:section]">
          {sections.map((section, index) => (
            <li key={section.id} className="flex">
              <a
                href={`#${section.id}`}
                className={cc([
                  "flex w-full flex-col items-center justify-center border-b-2 px-2 text-center before:mb-2 before:font-mono before:text-sm before:content-none",
                  {
                    "border-black bg-white/50 text-slate-900 before:text-white/40":
                      index === activeIndex,
                    "border-transparent before:text-slate-500 hover:before:text-slate-900":
                      index !== activeIndex,
                    "hover:bg-kkm-yellow/40": theme === "yellow",
                    "hover:bg-kkm-red/40": theme === "red",
                    "hover:bg-kkm-green/40": theme === "green",
                  },
                ])}
              >
                {section.title}
              </a>
            </li>
          ))}
        </ol>
      </div>
    </div>
  )
}
